import { useEffect, useRef } from 'react'
import type { JSX, ReactNode } from 'react'
import type { ApiFailure } from '@/api/client'
import { NETWORK_FAILURE } from '@/api/client'

/**
 * Empty, loading and error — defined once, for every screen.
 *
 * CLAUDE.md's frontend rules require all three of every component, and the
 * failure mode they are guarding against is not "a screen forgot one" but "six
 * screens each improvised one". These wrap the Atlas classes from
 * design/atlas/components.css, which is where the three states were designed
 * (S-D1), so a page never writes `.ar-skeleton` markup itself.
 */

/**
 * A skeleton that mirrors the shape of what is coming, so nothing reflows.
 *
 * `lines` rather than a fixed block: a table and a stat row are different
 * shapes, and a skeleton the wrong shape is a layout jump wearing a costume.
 */
export function Loading({
  label = 'Loading',
  lines = 3,
}: {
  label?: string
  lines?: number
}): JSX.Element {
  return (
    <div className="ar-card" aria-busy="true" aria-live="polite">
      <div className="ar-card__body ar-stack ar-stack--tight">
        <span className="ar-visually-hidden">{label}</span>
        <div className="ar-skeleton ar-skeleton--title" />
        {Array.from({ length: lines }, (_, index) => (
          <div
            key={index}
            className="ar-skeleton ar-skeleton--text"
            style={{ width: `${String(90 - index * 12)}%` }}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Never a bare "no data" — it says what would be here and how to get it
 * (design/atlas/components.css, `.ar-empty`).
 */
export function Empty({
  // The mockups' own glyph for "nothing here yet" (design/screens/seo.html,
  // seven of its empty states). `○` was the previous default and it rendered
  // as a dot inside the circle — a placeholder that looked like an icon that
  // had failed to load.
  icon = '⌕',
  title,
  body,
  action,
}: {
  icon?: string
  title: string
  body: string
  action?: ReactNode
}): JSX.Element {
  return (
    <div className="ar-empty">
      <div className="ar-empty__icon" aria-hidden="true">
        {icon}
      </div>
      <p className="ar-empty__title">{title}</p>
      <p className="ar-empty__body">{body}</p>
      {action}
    </div>
  )
}

/**
 * What failed, and a way forward.
 *
 * The request id is shown only when there is one. A failure that never reached
 * the API has no envelope and no id, and printing an empty "quote this to
 * support" line would send people to support with nothing to quote.
 */
export function ErrorState({
  failure,
  title = 'That did not work',
  onRetry,
}: {
  failure: ApiFailure
  title?: string
  onRetry?: () => void
}): JSX.Element {
  return (
    <div className="ar-error-state" role="alert">
      <div className="ar-error-state__icon" aria-hidden="true">
        !
      </div>
      <p className="ar-error-state__title">
        {failure.code === NETWORK_FAILURE ? 'The server is unreachable' : title}
      </p>
      <p className="ar-error-state__body">{failure.message}</p>
      {failure.requestId ? (
        <p className="ar-error-state__detail">Quote this to support: {failure.requestId}</p>
      ) : null}
      {onRetry ? (
        <button type="button" className="ar-btn ar-btn--ghost ar-btn--sm" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  )
}

/**
 * One failing field, named the way the person filling the form sees it.
 *
 * The server's own `fields` say `email` because that is the wire name; S-D2
 * screen 02's summary says *"That email is already registered"* under the
 * label the field carries. A screen that knows both is the only layer that can
 * translate, so it passes the translation in rather than this component
 * guessing at a label from a JSON pointer.
 */
export interface FieldProblem {
  /** The control's own label, so the summary and the field agree. */
  label: string
  /** What is wrong with it, in the same words the field's own error shows. */
  message: string
}

/**
 * A failure shown inside a form rather than in place of the screen.
 *
 * `role="alert"` and the Atlas treatment S-D1 designed for a failed submit —
 * the same whether the message came from the API or from validation at the
 * boundary. **Which** treatment depends on whether there is a list of failing
 * fields to show: `.ar-form-summary` when there is, `.ar-alert--danger` when
 * the failure is one sentence (AUTH-45; the reasoning is on the branch).
 *
 * **`problems` is the screen's own reading of the same failure** (AUTH-3). A
 * 409 `EMAIL_ALREADY_REGISTERED` carries no `details.fields` — only a 422 does —
 * so without this the summary is a single sentence and the form has no way to
 * say *which* of its inputs the sentence is about. When a screen supplies them
 * they replace the server's raw list rather than joining it: the two are the
 * same refusal read twice, and printing both would put `email: value is not a
 * valid email address` under *Work email: That address is already registered*.
 *
 * **`takesFocus` moves the keyboard to the summary** (AUTH-4; MOCKUP-METHOD,
 * *"a failed submit shows a summary that takes focus"*). Off by default and
 * opted into by the form screens, because this component is also how a
 * background refresh, a dropdown and a Markdown editor report a failure — and
 * a summary that grabs the caret out of an editor the moment a save fails is a
 * worse bug than the one it is announcing.
 */
export function InlineError({
  failure,
  problems = [],
  takesFocus = false,
}: {
  failure: ApiFailure | null
  problems?: readonly FieldProblem[]
  takesFocus?: boolean
}): JSX.Element | null {
  const summary = useRef<HTMLDivElement | null>(null)

  // Keyed on the failure object rather than on mounting, because a second
  // failed submit re-uses the element already on screen: `useMutation` hands
  // back a new `ApiFailure` each time, so this fires again where a mount-only
  // effect would fire once and leave every later attempt silent.
  useEffect(() => {
    if (takesFocus && failure) summary.current?.focus()
  }, [takesFocus, failure])

  if (!failure) return null
  const fields = failure.fields
  const listed = problems.length > 0 || fields.length > 0

  // AUTH-45. The design uses *two* components for a failed submit and picks
  // between them on exactly this question. Screen 02 — a signup refused over
  // two named fields — is `.ar-form-summary`: an `<h3>` and a `<ul>` of the
  // things to go and fix, in a 2px danger border, because the list is the
  // content. Screen 04 — a sign-in refused generically, deliberately saying
  // nothing about which half was wrong — is `.ar-alert--danger`: a hairline
  // rule, a `<strong>` line and a sentence, because there is nothing to
  // enumerate and a heavy summary box around one sentence overstates it.
  //
  // The app used the summary for both, so every screen with a single-sentence
  // failure was drawing the list treatment around an empty list.
  //
  // The request id is not what decides it. It is on nearly every failure and
  // is support's handle on the request, not a thing the reader can act on, so
  // it rides along in whichever shape was chosen rather than forcing the one
  // with a list in it.
  if (!listed) {
    return (
      <div
        className="ar-alert ar-alert--danger"
        role="alert"
        ref={summary}
        tabIndex={takesFocus ? -1 : undefined}
      >
        <span className="ar-alert__icon" aria-hidden="true">
          ✕
        </span>
        <div className="ar-alert__body">
          <strong>{failure.message}</strong>
          {/* The reference is for a fault somebody at AugRank would look up. A
              refusal the server chose — 4xx, with a sentence naming the remedy —
              is not one, and *Quote this to support* under *discovery only runs
              inside your send window* made a rule read as an outage
              (walkthrough 2026-09-12). */}
          {failure.requestId && failure.status >= 500 ? (
            <>
              Quote this to support: <span className="ar-mono">{failure.requestId}</span>
            </>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div
      className="ar-form-summary"
      role="alert"
      ref={summary}
      // Focusable by script, never by tabbing: the summary is a destination for
      // a failed submit, not a stop on the way through the form.
      tabIndex={takesFocus ? -1 : undefined}
    >
      <h3>{failure.message}</h3>
      <ul>
        {problems.map((problem) => (
          <li key={problem.label}>
            {problem.label}: {problem.message}
          </li>
        ))}
        {/* The server's own per-field reasons. Without these a 422 arrives as
              the fixed sentence "Request validation failed." and the person
              looking at it has to guess which field it means — which for a
              form with eleven inputs is not a guess anybody makes twice. */}
        {/* The index is in the key because pydantic can report two errors
              against the same field — a union that failed both arms — and two
              `<li>` sharing a key is a rendering bug rather than a duplicate. */}
        {problems.length > 0
          ? null
          : fields.map((field, index) => (
              <li key={`${field.loc.join('.')}:${String(index)}`}>
                {field.loc.at(-1) ?? 'Request'}: {field.msg}
              </li>
            ))}
        {failure.requestId ? (
          <li>
            Request <span className="ar-mono">{failure.requestId}</span>
          </li>
        ) : null}
      </ul>
    </div>
  )
}

/** The whole viewport, while the app works out who you are. */
export function FullPageLoading({ label = 'Loading' }: { label?: string }): JSX.Element {
  return (
    <div className="app-full-state" aria-busy="true" aria-live="polite">
      <span className="ar-spinner" aria-hidden="true" />
      <p className="ar-muted">{label}</p>
    </div>
  )
}
